import { useEffect, useState } from "react";
import { keywordSearch, profiles } from "../lib/api";
import type { KeywordSearch, KeywordSearchResult, Profile } from "../lib/api";
import {
  Plus,
  Trash2,
  RefreshCw,
  Search,
  Play,
  Pause,
  ExternalLink,
  Package,
  CheckCircle,
  XCircle,
} from "lucide-react";
import { format } from "date-fns";
import clsx from "clsx";

export default function SearchPage() {
  const [searches, setSearches] = useState<KeywordSearch[]>([]);
  const [profileList, setProfileList] = useState<Profile[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [selectedSearch, setSelectedSearch] = useState<KeywordSearch | null>(null);
  const [results, setResults] = useState<KeywordSearchResult[]>([]);

  // Form state
  const [name, setName] = useState("");
  const [retailer, setRetailer] = useState<"walmart" | "pokemoncenter">("walmart");
  const [keywords, setKeywords] = useState("");
  const [excludeKeywords, setExcludeKeywords] = useState("");
  const [minPrice, setMinPrice] = useState("");
  const [maxPrice, setMaxPrice] = useState("");
  const [profileId, setProfileId] = useState("");
  const [autoCreateTask, setAutoCreateTask] = useState(false);
  const [notifyDiscord, setNotifyDiscord] = useState(true);
  const [checkInterval, setCheckInterval] = useState("30");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const fetchData = async () => {
    try {
      const [searchData, profileData] = await Promise.all([
        keywordSearch.list(),
        profiles.list(),
      ]);
      setSearches(searchData);
      setProfileList(profileData);
    } catch (error) {
      console.error("Failed to fetch data", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    // Refresh every 10 seconds
    const interval = setInterval(fetchData, 10000);
    return () => clearInterval(interval);
  }, []);

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);

    try {
      await keywordSearch.create({
        name,
        retailer,
        keywords,
        excludeKeywords: excludeKeywords || undefined,
        minPrice: minPrice ? parseFloat(minPrice) : undefined,
        maxPrice: maxPrice ? parseFloat(maxPrice) : undefined,
        profileId: profileId || undefined,
        autoCreateTask,
        notifyDiscord,
        checkInterval: parseInt(checkInterval) * 1000,
      });
      setShowCreate(false);
      resetForm();
      fetchData();
    } catch (err) {
      setError((err as Error).message || "Failed to create search");
    } finally {
      setSubmitting(false);
    }
  };

  const resetForm = () => {
    setName("");
    setKeywords("");
    setExcludeKeywords("");
    setMinPrice("");
    setMaxPrice("");
    setProfileId("");
    setAutoCreateTask(false);
    setNotifyDiscord(true);
    setCheckInterval("30");
    setError("");
  };

  const handleToggle = async (search: KeywordSearch) => {
    try {
      if (search.enabled) {
        await keywordSearch.stop(search.id);
      } else {
        await keywordSearch.start(search.id);
      }
      fetchData();
    } catch (error) {
      console.error("Failed to toggle search", error);
    }
  };

  const handleRunNow = async (search: KeywordSearch) => {
    try {
      await keywordSearch.run(search.id);
      fetchData();
    } catch (error) {
      console.error("Failed to run search", error);
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm("Delete this keyword search?")) return;
    try {
      await keywordSearch.delete(id);
      fetchData();
    } catch (error) {
      console.error("Failed to delete search", error);
    }
  };

  const viewResults = async (search: KeywordSearch) => {
    setSelectedSearch(search);
    const data = await keywordSearch.getResults(search.id);
    setResults(data);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <RefreshCw className="animate-spin text-primary-500" size={32} />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-display text-xl font-semibold">
            Keyword Search ({searches.length})
          </h2>
          <p className="text-sm text-gray-400 mt-1">
            Monitor for products by keywords when no direct product page exists
          </p>
        </div>
        <button onClick={() => setShowCreate(true)} className="btn-primary flex items-center gap-2">
          <Plus size={18} />
          New Search
        </button>
      </div>

      {/* Create Modal */}
      {showCreate && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="card max-w-xl w-full max-h-[90vh] overflow-y-auto">
            <h3 className="font-display text-lg font-semibold mb-4">Create Keyword Search</h3>
            <form onSubmit={handleCreate} className="space-y-4">
              {error && (
                <div className="p-3 bg-error/20 border border-error/50 rounded-lg text-error text-sm">
                  {error}
                </div>
              )}

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="label">Name</label>
                  <input
                    type="text"
                    className="input"
                    placeholder="e.g., Temporal Forces ETB"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    required
                  />
                </div>
                <div>
                  <label className="label">Retailer</label>
                  <select
                    className="input"
                    value={retailer}
                    onChange={(e) => setRetailer(e.target.value as "walmart" | "pokemoncenter")}
                  >
                    <option value="walmart">Walmart</option>
                    <option value="pokemoncenter">Pokemon Center</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="label">Keywords (comma-separated)</label>
                <input
                  type="text"
                  className="input"
                  placeholder="temporal forces, elite trainer box"
                  value={keywords}
                  onChange={(e) => setKeywords(e.target.value)}
                  required
                />
                <p className="text-xs text-gray-500 mt-1">
                  All keywords must match (AND logic)
                </p>
              </div>

              <div>
                <label className="label">Exclude Keywords (optional)</label>
                <input
                  type="text"
                  className="input"
                  placeholder="damaged, used, bundle"
                  value={excludeKeywords}
                  onChange={(e) => setExcludeKeywords(e.target.value)}
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="label">Min Price ($)</label>
                  <input
                    type="number"
                    step="0.01"
                    className="input"
                    placeholder="0.00"
                    value={minPrice}
                    onChange={(e) => setMinPrice(e.target.value)}
                  />
                </div>
                <div>
                  <label className="label">Max Price ($)</label>
                  <input
                    type="number"
                    step="0.01"
                    className="input"
                    placeholder="100.00"
                    value={maxPrice}
                    onChange={(e) => setMaxPrice(e.target.value)}
                  />
                </div>
              </div>

              <div>
                <label className="label">Check Interval (seconds)</label>
                <input
                  type="number"
                  className="input"
                  min="10"
                  max="300"
                  value={checkInterval}
                  onChange={(e) => setCheckInterval(e.target.value)}
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="label">Auto-Create Task Profile</label>
                  <select
                    className="input"
                    value={profileId}
                    onChange={(e) => setProfileId(e.target.value)}
                  >
                    <option value="">None (manual)</option>
                    {profileList.map((p) => (
                      <option key={p.id} value={p.id}>
                        {p.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="flex flex-col justify-end gap-2">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={autoCreateTask}
                      onChange={(e) => setAutoCreateTask(e.target.checked)}
                      disabled={!profileId}
                      className="w-4 h-4 rounded"
                    />
                    <span className="text-sm">Auto-create task</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={notifyDiscord}
                      onChange={(e) => setNotifyDiscord(e.target.checked)}
                      className="w-4 h-4 rounded"
                    />
                    <span className="text-sm">Discord notifications</span>
                  </label>
                </div>
              </div>

              <div className="flex gap-3 justify-end pt-4 border-t border-dark-600">
                <button
                  type="button"
                  onClick={() => {
                    setShowCreate(false);
                    resetForm();
                  }}
                  className="btn-secondary"
                  disabled={submitting}
                >
                  Cancel
                </button>
                <button type="submit" className="btn-primary" disabled={submitting}>
                  {submitting ? "Creating..." : "Create Search"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Results Modal */}
      {selectedSearch && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="card max-w-3xl w-full max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-display text-lg font-semibold">
                Results: {selectedSearch.name}
              </h3>
              <button
                onClick={() => setSelectedSearch(null)}
                className="p-2 hover:bg-dark-600 rounded-lg"
              >
                <XCircle size={20} />
              </button>
            </div>

            {results.length === 0 ? (
              <div className="text-center py-8 text-gray-400">
                <Search size={48} className="mx-auto mb-4 opacity-50" />
                <p>No products found yet</p>
              </div>
            ) : (
              <div className="space-y-3">
                {results.map((result) => (
                  <div key={result.id} className="p-3 bg-dark-700 rounded-lg flex gap-4">
                    {result.imageUrl ? (
                      <img
                        src={result.imageUrl}
                        alt={result.name}
                        className="w-16 h-16 rounded object-cover"
                      />
                    ) : (
                      <div className="w-16 h-16 bg-dark-600 rounded flex items-center justify-center">
                        <Package size={24} className="text-gray-500" />
                      </div>
                    )}
                    <div className="flex-1">
                      <h4 className="font-medium line-clamp-2">{result.name}</h4>
                      <div className="flex items-center gap-3 mt-1">
                        <span
                          className={clsx(
                            "status-badge text-xs",
                            result.inStock
                              ? "bg-success/20 text-success"
                              : "bg-error/20 text-error"
                          )}
                        >
                          {result.inStock ? "In Stock" : "Out of Stock"}
                        </span>
                        {result.price && (
                          <span className="text-primary-500 font-semibold">
                            ${result.price.toFixed(2)}
                          </span>
                        )}
                        <span className="text-xs text-gray-500">
                          Found {format(new Date(result.firstFoundAt), "MMM d, h:mm a")}
                        </span>
                      </div>
                    </div>
                    <a
                      href={result.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="btn-secondary p-2"
                    >
                      <ExternalLink size={16} />
                    </a>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Search List */}
      {searches.length === 0 ? (
        <div className="card text-center py-12">
          <Search size={48} className="mx-auto text-gray-600 mb-4" />
          <p className="text-gray-400">
            No keyword searches yet. Create one to start monitoring!
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {searches.map((search) => (
            <div key={search.id} className="card">
              <div className="flex items-start justify-between">
                <div>
                  <div className="flex items-center gap-3">
                    <h3 className="font-semibold">{search.name}</h3>
                    <span
                      className={clsx(
                        "status-badge text-xs",
                        search.status === "searching" && "bg-info/20 text-info",
                        search.status === "found" && "bg-success/20 text-success",
                        search.status === "error" && "bg-error/20 text-error",
                        search.status === "idle" && "bg-gray-500/20 text-gray-400"
                      )}
                    >
                      {search.status}
                    </span>
                    <span className="text-xs text-gray-500">{search.retailer}</span>
                  </div>
                  <p className="text-sm text-gray-400 mt-1">
                    Keywords: <span className="text-primary-400">{search.keywords}</span>
                  </p>
                  {search.excludeKeywords && (
                    <p className="text-xs text-gray-500">
                      Excluding: {search.excludeKeywords}
                    </p>
                  )}
                  <div className="flex items-center gap-4 mt-2 text-xs text-gray-500">
                    <span>Check every {search.checkInterval / 1000}s</span>
                    {search.lastChecked && (
                      <span>
                        Last: {format(new Date(search.lastChecked), "h:mm:ss a")}
                      </span>
                    )}
                    <span>Found: {search.lastFoundCount} products</span>
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  <button
                    onClick={() => viewResults(search)}
                    className="btn-secondary p-2"
                    title="View Results"
                  >
                    <Search size={16} />
                  </button>
                  <button
                    onClick={() => handleRunNow(search)}
                    className="btn-secondary p-2"
                    title="Run Now"
                  >
                    <RefreshCw size={16} />
                  </button>
                  <button
                    onClick={() => handleToggle(search)}
                    className={clsx(
                      "p-2 rounded-lg transition-colors",
                      search.enabled
                        ? "bg-success/20 text-success hover:bg-success/30"
                        : "bg-dark-600 text-gray-400 hover:bg-dark-500"
                    )}
                    title={search.enabled ? "Pause" : "Start"}
                  >
                    {search.enabled ? <Pause size={16} /> : <Play size={16} />}
                  </button>
                  <button
                    onClick={() => handleDelete(search.id)}
                    className="p-2 hover:bg-dark-600 rounded-lg transition-colors"
                    title="Delete"
                  >
                    <Trash2 size={16} className="text-error" />
                  </button>
                </div>
              </div>

              {/* Recent Found Products */}
              {search.foundProducts && search.foundProducts.length > 0 && (
                <div className="mt-4 pt-4 border-t border-dark-600">
                  <p className="text-xs text-gray-500 mb-2">Recent finds:</p>
                  <div className="flex gap-2 flex-wrap">
                    {search.foundProducts.slice(0, 5).map((p) => (
                      <a
                        key={p.id}
                        href={p.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={clsx(
                          "text-xs px-2 py-1 rounded flex items-center gap-1",
                          p.inStock
                            ? "bg-success/20 text-success"
                            : "bg-dark-600 text-gray-400"
                        )}
                      >
                        {p.inStock ? (
                          <CheckCircle size={12} />
                        ) : (
                          <XCircle size={12} />
                        )}
                        {p.name.substring(0, 30)}...
                      </a>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

